import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, MapPin as MapPinIcon, Map as MapIcon } from "lucide-react";
import Seo from "@/components/site/Seo";
import { CLIENT_PROJECTS } from "@/data/projects";
import api from "@/lib/api";

const ProjectsMap = lazy(() => import("@/components/site/ProjectsMap"));

const CATEGORIES = ["All", "New Build", "Renovation", "Extension"];
const AREAS_LIST = [
  { slug: "all", label: "All areas" },
  { slug: "wembley", label: "Wembley" },
  { slug: "ealing", label: "Ealing" },
  { slug: "richmond", label: "Richmond" },
  { slug: "chiswick", label: "Chiswick" },
  { slug: "wimbledon", label: "Wimbledon" },
  { slug: "hampstead", label: "Hampstead" },
  { slug: "croydon", label: "Croydon" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (img) => (img && img.startsWith("/api/") ? `${BACKEND_URL}${img}` : img);

export default function Projects() {
  const [category, setCategory] = useState("All");
  const [areaSlug, setAreaSlug] = useState("all");
  const [query, setQuery] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [adminProjects, setAdminProjects] = useState([]);

  useEffect(() => {
    api
      .get("/projects")
      .then((r) => Array.isArray(r.data) && setAdminProjects(r.data))
      .catch(() => {});
  }, []);

  const all = useMemo(() => {
    return [...adminProjects, ...CLIENT_PROJECTS];
  }, [adminProjects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((p) => {
      if (category !== "All" && p.category !== category) return false;
      if (areaSlug !== "all" && p.area_slug !== areaSlug) return false;
      if (q) {
        const hay = `${p.title} ${p.location} ${p.category} ${p.year} ${p.blurb || p.description || ""} ${p.area_slug || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [all, category, areaSlug, query]);

  return (
    <div data-testid="projects-page">
      <Seo
        title="Recent Projects — Renovations, Extensions & Builds Across London"
        description="Portfolio of Prime Build Design LTD residential construction projects across Wembley, Ealing, Richmond, Chiswick, Wimbledon, Hampstead and Croydon. New builds, renovations, kitchen extensions and loft conversions."
        path="/projects"
      />

      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28">
          <div className="overline mb-6">Recent projects</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05] max-w-4xl">
            Behind the<br /> craftsmanship.
          </h1>
          <p className="mt-6 text-slate-600 text-lg max-w-2xl leading-relaxed">
            Every photo below is from a real Prime Build Design site. Filter by area, service or search — or view the interactive map to see where we build across London.
          </p>
        </div>
      </section>

      {/* Filters bar */}
      <section className="bg-white border-b border-slate-200 sticky top-0 z-20" data-testid="project-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row gap-3 md:items-center">
          {/* Search */}
          <label className="relative flex-1 min-w-0" data-testid="project-search">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search: kitchen extension, Ealing, W5 …"
              className="field pl-10 !h-11"
              data-testid="project-search-input"
              aria-label="Search projects"
            />
          </label>
          {/* Area select */}
          <select
            value={areaSlug}
            onChange={(e) => setAreaSlug(e.target.value)}
            className="field !h-11 md:w-48"
            aria-label="Filter by area"
            data-testid="project-area-select"
          >
            {AREAS_LIST.map((a) => <option key={a.slug} value={a.slug}>{a.label}</option>)}
          </select>
          {/* Category chips */}
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter by service type">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 h-11 text-sm font-medium tracking-wide transition ${
                  category === c
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowMap((v) => !v)}
            data-testid="project-map-toggle"
            className={`inline-flex items-center gap-2 px-4 h-11 border transition text-sm font-medium ${
              showMap ? "bg-[#C5A059] text-white border-[#C5A059]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
            aria-pressed={showMap}
          >
            <MapIcon size={16} /> {showMap ? "Hide map" : "Map view"}
          </button>
        </div>
      </section>

      {/* Map (lazy-loaded chunk — only when the user asks for it) */}
      {showMap && (
        <section className="bg-slate-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="overline mb-3 flex items-center gap-1">
              <MapPinIcon size={14} className="text-[#C5A059]" /> Projects across London
            </div>
            <Suspense fallback={<div className="h-[520px] w-full bg-slate-100 flex items-center justify-center text-slate-500">Loading map…</div>}>
              <ProjectsMap projects={filtered} />
            </Suspense>
          </div>
        </section>
      )}

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-sm text-slate-500 mb-6" data-testid="project-count">
            {filtered.length === 0 ? "No projects match your filters yet." : `Showing ${filtered.length} project${filtered.length === 1 ? "" : "s"}`}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <Link
                key={p.slug || p.id}
                to={`/projects/${p.slug || p.id}`}
                data-testid={`project-card-${p.slug || p.id}`}
                className="group relative h-[420px] overflow-hidden text-left focus:outline focus:outline-2 focus:outline-[#C5A059]"
                aria-label={`View project: ${p.title}`}
              >
                <img
                  src={resolveImg(p.img)}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/92 via-slate-900/25 to-transparent" />
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center">
                    <ArrowUpRight size={16} />
                  </div>
                </div>
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="overline text-white/70">{p.category} · {p.year}</div>
                  <div className="font-display text-2xl font-semibold mt-1 leading-snug">{p.title}</div>
                  <div className="text-xs text-white/80 mt-1 inline-flex items-center gap-1">
                    <MapPinIcon size={11} /> {p.location}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center text-slate-500 py-16" data-testid="projects-empty">
              Try clearing your filters, or <Link to="/contact" className="underline">get in touch for tailored examples</Link>.
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white">Yours could be next.</h2>
          <Link to="/contact" className="btn-primary mt-8 inline-flex" data-testid="projects-cta-contact">
            Start a Conversation
          </Link>
        </div>
      </section>
    </div>
  );
}
